import Api from "./Api/Api";

/**
 * Bestsellers: the brands that actually sold most over the last 90 days, from
 * GET /marketplace/v3/bestsellers/ (public). Replaces the hand-picked
 * "Trending" row on the marketplace and leads its "best prices" grid.
 *
 * Each item has a kind: "bestseller" (ranked by sales) or "pick" (a Fleek
 * pick, placed after the bestsellers; normally 3 + 2). `rank` is the 1-based
 * position across the whole list, so picks arrive as rank 4 and 5. A missing
 * or unknown kind is a bestseller: the API had no kind before picks existed.
 * Only bestsellers are numbered or earn the "Bestseller" badge, and they are
 * numbered among bestsellers (bestsellerRank), never counting picks.
 *
 * Everything here is pure except loadBestsellers, which fetches once per
 * session and never throws: a bestseller surface is a nice-to-have and must
 * never block or break the page it sits on.
 */

// Fewer than this and the strip looks broken rather than curated; hide it.
export const MIN_STRIP_ITEMS = 3;
// Bestsellers ranked up to this (among bestsellers, picks never count) get the
// "Bestseller" badge on grid cards and brand pages.
export const BADGE_TOP_N = 5;

export const KIND_BESTSELLER = "bestseller";
export const KIND_PICK = "pick";

const toKind = (v) => (v === KIND_PICK ? KIND_PICK : KIND_BESTSELLER);

const positiveInt = (v) => {
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
};

// starts_from arrives as vendor_v2's pre-formatted label ("₹99/mo",
// "₹649/6 mo"); a bare number (649) is accepted too. Keep the number and,
// separately, the period after the "/", so "₹99/mo" never reads as ₹99 flat.
const toPeriod = (v) => {
  if (v === null || v === undefined || typeof v === "number") return "";
  const parts = String(v).split("/");
  return parts.length > 1 ? parts.slice(1).join("/").trim() : "";
};

const toPrice = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) && v >= 0 ? v : null;
  const match = String(v).match(/([\d,]+(?:\.\d+)?)/);
  if (!match) return null;
  const n = Number(match[1].replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
};

/**
 * The response (or whatever the app's axios interceptor resolved with on a
 * failure) to a clean list sorted by rank. Items that can't link to a brand
 * page (no vendor_web_path, the only thing the /brand route takes) or have no
 * name are dropped; a repeated vendor keeps its best rank.
 *
 * bestsellerRank is a bestseller's rank with the picks ranked ahead of it
 * taken out (null on a pick). With the contract's bestsellers-then-picks order
 * it is just the rank; a dropped bestseller still leaves its gap, so the badge
 * never slides down to a brand that didn't earn it.
 */
export const parseBestsellers = (response) => {
  const body = response?.data;
  const results = Array.isArray(body?.results) ? body.results : [];

  const items = results
    .map((r, i) => {
      if (!r || typeof r !== "object") return null;
      const vendorId = positiveInt(r.vendor_id);
      const path = typeof r.vendor_web_path === "string" ? r.vendor_web_path.trim() : "";
      const name = typeof r.name === "string" ? r.name.trim() : "";
      if (!vendorId || !path || !name) return null;
      return {
        rank: positiveInt(r.rank) || i + 1,
        kind: toKind(r.kind),
        vendorId,
        serviceId: positiveInt(r.service_id),
        name,
        logoUrl: typeof r.logo_url === "string" && r.logo_url ? r.logo_url : null,
        category: typeof r.category === "string" ? r.category : "",
        startsFrom: toPrice(r.starts_from),
        period: toPeriod(r.starts_from),
        vendorWebPath: path,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.rank - b.rank);

  const seen = new Set();
  const unique = items.filter((it) => {
    if (seen.has(it.vendorId) || seen.has(it.vendorWebPath)) return false;
    seen.add(it.vendorId);
    seen.add(it.vendorWebPath);
    return true;
  });

  let picksAhead = 0;
  return unique.map((it) => {
    if (it.kind === KIND_PICK) {
      picksAhead += 1;
      return { ...it, bestsellerRank: null };
    }
    return { ...it, bestsellerRank: it.rank - picksAhead };
  });
};

export const isPick = (item) => item?.kind === KIND_PICK;

export const countByKind = (list) =>
  (list || []).reduce(
    (acc, item) => {
      if (isPick(item)) acc.picks += 1;
      else acc.bestsellers += 1;
      return acc;
    },
    { bestsellers: 0, picks: 0 }
  );

// At least MIN_STRIP_ITEMS in all, and at least one real bestseller: picks
// alone must never sit under a "Bestsellers" heading.
export const shouldShowStrip = (list) =>
  Array.isArray(list) && list.length >= MIN_STRIP_ITEMS && list.some((item) => !isPick(item));

// A vendor_v2 brand row is the same brand as a bestseller when either the
// vendor id or the web path matches.
const sameBrand = (brand, item) =>
  !!brand &&
  (Number(brand.vendor_id) === item.vendorId ||
    (!!brand.vendor_web_path && brand.vendor_web_path === item.vendorWebPath));

export const findBestseller = (list, { vendorId, vendorWebPath } = {}) =>
  (list || []).find((item) =>
    sameBrand({ vendor_id: vendorId, vendor_web_path: vendorWebPath }, item)
  ) || null;

/**
 * The rank among bestsellers when it earns a badge (top BADGE_TOP_N
 * bestsellers), else null. Picks never get one.
 */
export const badgeRank = (list, brand) => {
  const item = findBestseller(list, brand);
  if (!item || isPick(item)) return null;
  const rank = item.bestsellerRank ?? item.rank;
  return rank <= BADGE_TOP_N ? rank : null;
};

/**
 * The All view of the "best prices" grid: bestsellers that are in the loaded
 * brands, in rank order; then picks (unbadged), in rank order; then the
 * curated popular paths not already placed, in curated order; then
 * everything else in the order given (by discount). With no bestsellers this
 * is exactly the curated order it replaced.
 */
export const orderLeadBrands = ({ brands = [], rest = [], bestsellers = [], curatedPaths = [] }) => {
  const placed = new Set();
  const out = [];
  const take = (brand) => {
    if (!brand) return;
    const key = brand.vendor_web_path || `id:${brand.vendor_id}`;
    if (placed.has(key)) return;
    placed.add(key);
    out.push(brand);
  };

  const list = bestsellers || [];
  const lead = [...list.filter((item) => !isPick(item)), ...list.filter(isPick)];
  lead.forEach((item) => take(brands.find((b) => sameBrand(b, item))));
  (curatedPaths || []).forEach((path) => take(brands.find((b) => b.vendor_web_path === path)));
  (rest || []).forEach(take);
  return out;
};

/* ---------------- fetch ---------------- */

let pending = null;

/**
 * Bestsellers for this session. One request shared by every caller; resolves
 * [] on any failure (and forgets it, so the next page can try again).
 */
export const loadBestsellers = () => {
  if (!pending) {
    const request = Promise.resolve()
      .then(() => Api.Bestsellers())
      .then(parseBestsellers)
      .catch(() => []);
    pending = request;
    request.then((list) => {
      if (!list.length && pending === request) pending = null;
    });
  }
  return pending;
};

// Tests only.
export const resetBestsellers = () => {
  pending = null;
};
